import * as React from "react";

import { computeFitCount } from "../../hooks/use-fit-count";

/** The strip cluster's `gap="xs"`. */
export const STRIP_GAP = 4;

/** The ⋯ More control (labeled ghost + its hairline, gaps included) before it
 *  has ever rendered — replaced by its measured width after the first fold. */
const MORE_RESERVE_ESTIMATE = 112;

/** Marks a seated labeled action: its width is what the fold weighs. */
export const STRIP_ACTION_ATTR = "data-strip-action";
/** Marks in-flow width no fold reclaims: the destructive glyphs, and the
 *  More control when overflow actions already mount it. */
export const STRIP_FIXED_ATTR = "data-strip-fixed";
/** Marks the More control when a FOLD is what mounted it — weighed as the
 *  reserve, never as fixed, so seating one more action can't unmount the
 *  very control that made room for it. */
export const STRIP_MORE_ATTR = "data-strip-more";

function sumWidths(node: HTMLElement, attr: string): number {
  let total = 0;
  node.querySelectorAll<HTMLElement>(`[${attr}]`).forEach((el) => {
    total += el.offsetWidth + STRIP_GAP;
  });
  return total;
}

/**
 * How many of the strip's labeled actions fit the cluster, first to last;
 * the rest fold into More. The cluster is parent-driven (`flex: 1 1 0`,
 * `min-width: 0`), so its width never depends on how many actions it seats
 * and the measurement cannot loop. Widths are cached by action id: a folded
 * action is not in the DOM, so its last measured width stands in until it
 * is seated again. Measured before paint on every render — an action that
 * appears unmeasured renders once, is weighed, and folds in the same frame.
 */
export function useStripFold(
  cluster: React.RefObject<HTMLElement | null>,
  ids: readonly string[],
  {
    reserveMore,
  }: {
    /** False when overflow actions already mount More (it is then in-flow,
     *  marked fixed); true when only a fold would, so its width is held
     *  back for as long as anything stays hidden. */
    reserveMore: boolean;
  },
): number {
  const [count, setCount] = React.useState(Number.MAX_SAFE_INTEGER);
  const widthsRef = React.useRef(new Map<string, number>());
  const moreRef = React.useRef(MORE_RESERVE_ESTIMATE);
  const idsRef = React.useRef(ids);
  idsRef.current = ids;
  const reserveMoreRef = React.useRef(reserveMore);
  reserveMoreRef.current = reserveMore;

  const recompute = React.useCallback(() => {
    const node = cluster.current;
    if (!node) return;
    node
      .querySelectorAll<HTMLElement>(`[${STRIP_ACTION_ATTR}]`)
      .forEach((el) => {
        const id = el.getAttribute(STRIP_ACTION_ATTR);
        if (id) widthsRef.current.set(id, el.offsetWidth);
      });
    const more = node.querySelector<HTMLElement>(`[${STRIP_MORE_ATTR}]`);
    if (more) moreRef.current = more.offsetWidth + STRIP_GAP;
    const capacity = node.clientWidth;
    // A cluster that has not laid out yet (0) seats everything: nothing to
    // weigh against.
    if (capacity === 0) return;
    const widths = idsRef.current.map((id) => widthsRef.current.get(id) ?? 0);
    const next = computeFitCount({
      widths,
      capacity,
      gap: STRIP_GAP,
      fixed: sumWidths(node, STRIP_FIXED_ATTR),
      reserve: (hidden) =>
        hidden > 0 && reserveMoreRef.current ? moreRef.current : 0,
    });
    setCount((prev) => (prev === next ? prev : next));
  }, [cluster]);

  // Watch the cluster element that is mounted NOW. The bar renders nothing
  // while the selection is empty, so the cluster can mount (and remount)
  // long after this hook first runs; an observer attached only on the first
  // run would miss it, and a resize with no React render would never fold.
  const observerRef = React.useRef<{ node: HTMLElement; observer: ResizeObserver } | null>(null);

  // Every render: a seated action may have changed label or just mounted,
  // and the cluster element may be a new one.
  React.useLayoutEffect(() => {
    const node = cluster.current;
    const watched = observerRef.current;
    if (watched?.node !== node) {
      watched?.observer.disconnect();
      observerRef.current = null;
      if (node && typeof ResizeObserver !== "undefined") {
        const observer = new ResizeObserver(recompute);
        observer.observe(node);
        observerRef.current = { node, observer };
      }
    }
    recompute();
  });

  React.useLayoutEffect(
    () => () => {
      observerRef.current?.observer.disconnect();
      observerRef.current = null;
    },
    [],
  );

  return count;
}
